


import { Component, Vue, Watch } from "vue-property-decorator";

import Chart from "chart.js/auto";

import { Revenue } from "App";
import { EventBus } from "App/IOC";

import RevenueModel, {
    FailedPayment,
    Metric,
    StatusCount,
    PlanCount,
    RecurrenceMrr,
    TopCustomer
} from "App/Models/Revenue";

import DashboardControls from "./DashboardControls.vue";

/* Status -> swatch. Falls back to the muted grey for anything unmapped. */
const STATUS_COLORS: { [ key: string ]: string } = {
    Active: "#2E9C47",
    Trial: "#FF6C00",
    Offer: "#F5B700",
    Unpaid: "#D93343",
    Canceled: "#9A9EA2",
    Expired: "#C7CBD1"
};

/* Cards no longer shown ( Fred, 02/10/2026: a customer has one subscription, « Nouveaux
   abonnements » repeated « Nouveaux clients » ), even when an older backend still sends them. */
const HIDDEN_METRICS: string[] = [ "NewSubscriptions" ];

/* Cards where a rise is bad news: their variation shows red when it goes up. */
const RISE_IS_BAD: string[] = [ "Cancellations" ];

/* Cards whose value reads with cents, as Stripe shows them. */
const WITH_CENTS: string[] = [ "MRR", "AverageRevenue" ];

/* Recurrence -> swatch for the MRR split doughnut. */
const RECURRENCE_COLORS: { [ key: string ]: string } = {
    Monthly: "#FF6C00",
    Yearly: "#2E9C47"
};

@Component( { components: { DashboardControls } } )
export default class HomeView extends Vue {
    private loading: boolean = true;
    /* Control-bar state, owned by the parent. Null until the first snapshot resolves and
       seeds it from the window the backend echoed back. */
    private params: {
        From: string;
        To: string;
        Granularity: "day" | "week" | "month";
        Compare: "previous" | "year";
        Preset: string;
    } | null = null;
    /* Initialized to null, not undefined: vue-class-component won't make an
       undefined-initialized property reactive, so the view would never re-render. */
    private snapshot: RevenueModel | null = null;

    /* Live Chart.js instances, destroyed before each rebuild and on teardown. */
    private statusChart: Chart | null = null;
    private planChart: Chart | null = null;
    private recurrenceChart: Chart | null = null;
    /* One sparkline per metric card; rebuilt as a batch. */
    private metricCharts: Chart[] = [];

    private async created(): Promise<void> {
        /* First load: no params, so the backend applies its defaults and echoes back the
           resolved window we use to seed the control bar. */
        const [ snapshot, error ] = await Revenue.Snapshot();

        this.loading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.snapshot = snapshot === undefined ? null : snapshot;

        if( this.snapshot !== null )
            this.seedParams( this.snapshot );
    }

    /* Seeds the control-bar state from the window the backend resolved. Falls back to safe
       defaults when a field is absent (controls just won't highlight a matching preset). */
    private seedParams( snapshot: RevenueModel ): void {
        this.params = {
            From: snapshot.From === undefined ? "" : snapshot.From,
            To: snapshot.To === undefined ? "" : snapshot.To,
            Granularity: snapshot.Granularity === undefined ? "month" : snapshot.Granularity,
            Compare: snapshot.Compare === undefined ? "previous" : snapshot.Compare,
            Preset: "yearToDate"
        };
    }

    /* User changed the date/granularity/compare controls: adopt the new params and refetch.
       Setting `snapshot` re-triggers @Watch("snapshot") which rebuilds every chart. */
    private async onControlsChange( payload: {
        From: string;
        To: string;
        Granularity: "day" | "week" | "month";
        Compare: "previous" | "year";
        Preset: string;
    } ): Promise<void> {
        this.params = { ...payload };

        this.loading = true;

        const [ snapshot, error ] = await Revenue.Snapshot( {
            From: payload.From,
            To: payload.To,
            Granularity: payload.Granularity,
            Compare: payload.Compare
        } );

        this.loading = false;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.snapshot = snapshot === undefined ? null : snapshot;
    }

    private beforeDestroy(): void {
        this.destroyCharts();
    }

    /* Rebuild charts once the snapshot lands (canvas only exists after v-if flips). */
    @Watch( "snapshot" )
    private onSnapshot(): void {
        if( this.snapshot === null )
            return;

        this.$nextTick( () => this.buildCharts() );
    }

    /* Whole euros with the French thousands separator (headline figures, cents add noise). */
    private euro( value: number ): string {
        return `${ Math.round( value ).toLocaleString( "fr-FR" ) } €`;
    }

    /* Euros with cents ( MRR, average revenue ), as Stripe shows them. */
    private euroCents( value: number ): string {
        return `${ value.toLocaleString( "fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        } ) } €`;
    }

    /* A count the backend may not send yet ( older deployment ): a dash until then. */
    private orDash( value: number | undefined ): string {
        return value === undefined ? "—" : String( value );
    }

    /* "+393 €" / "-393 €" for the MRR gap. */
    private signedEuro( value: number ): string {
        return ( value > 0 ? "+" : "" ) + this.euroCents( value );
    }

    /* "13 août à 19:54". */
    private dateTime( iso: string ): string {
        const date = new Date( iso );
        const day = date.toLocaleDateString( "fr-FR", { day: "numeric", month: "short" } );
        const time = date.toLocaleTimeString( "fr-FR", { hour: "2-digit", minute: "2-digit" } );

        return `${ day } à ${ time }`;
    }

    /* The MRR gap worth showing: a euro or more, Stripe read. */
    private get mrrGap(): number | null {
        if( this.snapshot === null
            || this.snapshot.MRRGap === undefined
            || this.snapshot.MRRGap === null )
            return null;

        return Math.abs( this.snapshot.MRRGap ) >= 1 ? this.snapshot.MRRGap : null;
    }

    private get failedPayments(): FailedPayment[] {
        if( this.snapshot === null || this.snapshot.FailedPayments === undefined )
            return [];

        return this.snapshot.FailedPayments;
    }

    private get topCustomers(): TopCustomer[] {
        if( this.snapshot === null || this.snapshot.TopCustomers === undefined )
            return [];

        return this.snapshot.TopCustomers;
    }

    /* Compact euros for chart axes: 12 500 -> "12,5 k€". */
    private euroShort( value: number ): string {
        if( Math.abs( value ) >= 1000 ) {
            const k = ( value / 1000 ).toLocaleString( "fr-FR", { maximumFractionDigits: 1 } );

            return `${ k } k€`;
        }

        return `${ Math.round( value ) } €`;
    }

    /* "2026-06" -> "juin 26". */
    private monthLabel( month: string ): string {
        const [ year, monthIndex ] = month.split( "-" ).map( Number );
        const date = new Date( year, monthIndex - 1, 1 );

        const label = date.toLocaleDateString( "fr-FR", { month: "short" } );

        return `${ label } ${ String( year ).slice( 2 ) }`;
    }

    /* Granularity-aware x-axis label for a bucket-start ISO date "YYYY-MM-DD":
       month -> "juin 26", week/day -> "12 juin". Built from split parts so the parse is
       timezone-safe (no Date.parse UTC shift). */
    private formatBucketLabel( iso: string, granularity: string ): string {
        const [ year, monthIndex, day ] = iso.split( "-" ).map( Number );
        const date = new Date( year, monthIndex - 1, day );

        if( granularity === "month" ) {
            const month = date.toLocaleDateString( "fr-FR", { month: "short" } );

            return `${ month } ${ String( year ).slice( 2 ) }`;
        }

        return date.toLocaleDateString( "fr-FR", { day: "numeric", month: "short" } );
    }

    private get metrics(): Metric[] {
        if( this.snapshot === null || this.snapshot.Metrics === undefined )
            return [];

        return this.snapshot.Metrics.filter(
            metric => HIDDEN_METRICS.indexOf( metric.Key ) === -1 );
    }

    private get statuses(): StatusCount[] {
        if( this.snapshot === null || this.snapshot.SubscriptionsByStatus === undefined )
            return [];

        return this.snapshot.SubscriptionsByStatus;
    }

    private get subscriptionsByPlan(): PlanCount[] {
        if( this.snapshot === null || this.snapshot.SubscriptionsByPlan === undefined )
            return [];

        return this.snapshot.SubscriptionsByPlan;
    }

    private get mrrByRecurrence(): RecurrenceMrr[] {
        if( this.snapshot === null || this.snapshot.MrrByRecurrence === undefined )
            return [];

        return this.snapshot.MrrByRecurrence;
    }

    /* Per-metric percentage change vs the previous period. Returns undefined when there is
       no meaningful comparison (previous 0 and current 0). */
    private metricDelta( metric: Metric ): number | undefined {
        if( metric.Previous === 0 )
            return metric.Value > 0 ? 100 : undefined;

        return ( ( metric.Value - metric.Previous ) / metric.Previous ) * 100;
    }

    /* Delta chip label, e.g. "+12.50 %" or "+100 %" for a from-zero jump. */
    private metricDeltaLabel( metric: Metric ): string {
        if( metric.Previous === 0 )
            return metric.Value > 0 ? "+100 %" : "";

        const pct = this.metricDelta( metric );

        if( pct === undefined )
            return "";

        const sign = pct >= 0 ? "+" : "";

        return `${ sign }${ pct.toFixed( 2 ) } %`;
    }

    /* Green when the change is good news: a rise, except on the cards where a rise is bad. */
    private metricDeltaUp( metric: Metric ): boolean {
        const pct = this.metricDelta( metric );

        if( pct === undefined || pct === 0 )
            return true;

        return RISE_IS_BAD.indexOf( metric.Key ) === -1 ? pct > 0 : pct < 0;
    }

    /* Headline figure for a metric card. */
    private metricValue( metric: Metric ): string {
        return this.metricAmount( metric, metric.Value );
    }

    /* Previous-period figure shown in the card subtitle. */
    private metricPrevious( metric: Metric ): string {
        return this.metricAmount( metric, metric.Previous );
    }

    private metricAmount( metric: Metric, value: number ): string {
        if( metric.Unit !== "euro" )
            return String( value );

        return WITH_CENTS.indexOf( metric.Key ) !== -1
            ? this.euroCents( value )
            : this.euro( value );
    }

    /* Reads the active theme's semantic colours so charts follow light/dark. */
    private themeColor( name: string, fallback: string ): string {
        const value = getComputedStyle( document.documentElement )
            .getPropertyValue( name )
            .trim();

        return value !== "" ? value : fallback;
    }

    private destroyCharts(): void {
        for( const chart of this.metricCharts )
            chart.destroy();

        this.metricCharts = [];

        if( this.statusChart !== null )
            this.statusChart.destroy();

        if( this.planChart !== null )
            this.planChart.destroy();

        if( this.recurrenceChart !== null )
            this.recurrenceChart.destroy();

        this.statusChart = null;
        this.planChart = null;
        this.recurrenceChart = null;
    }

    private buildCharts(): void {
        if( this.snapshot === null )
            return;

        this.destroyCharts();

        this.buildMetricCharts();
        this.buildStatusChart();
        this.buildPlanChart();
        this.buildRecurrenceChart();
    }

    private buildMetricCharts(): void {
        for( const metric of this.metrics ) {
            const reference = this.$refs[ "metricCanvas-" + metric.Key ];
            const canvas = ( Array.isArray( reference )
                ? reference[ 0 ]
                : reference ) as HTMLCanvasElement | undefined;

            if( canvas === undefined )
                continue;

            const chart = this.buildMetricChart( canvas, metric );

            if( chart !== null )
                this.metricCharts.push( chart );
        }
    }

    private buildMetricChart( canvas: HTMLCanvasElement, metric: Metric ): Chart | null {
        const accent = this.themeColor( "--accent", "#FF6C00" );
        const grid = this.themeColor( "--border-subtle", "#EDEDED" );
        const muted = this.themeColor( "--fg-muted", "#9A9EA2" );

        const isEuro = metric.Unit === "euro";
        const granularity = ( this.snapshot !== null && this.snapshot.Granularity !== undefined )
            ? this.snapshot.Granularity
            : "month";
        const labels = metric.Labels.map(
            bucket => this.formatBucketLabel( bucket, granularity ) );

        /* Soft vertical gradient under the line, Stripe-style. */
        const context = canvas.getContext( "2d" );
        const fill = context !== null ? context.createLinearGradient( 0, 0, 0, 176 ) : accent;

        if( typeof fill !== "string" ) {
            fill.addColorStop( 0, this.hexToRgba( accent, 0.18 ) );
            fill.addColorStop( 1, this.hexToRgba( accent, 0 ) );
        }

        const datasets: Chart[ "data" ][ "datasets" ] = [ {
            label: this.$t( "HOME_metric_" + metric.Key ) as string,
            data: metric.Series,
            borderColor: accent,
            backgroundColor: fill,
            borderWidth: 2,
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: accent
        } ];

        /* Previous-period overlay (dotted, muted, no fill). */
        if( metric.PreviousSeries.length > 0 ) {
            datasets.push( {
                label: this.$t( "HOME_previousPeriod" ) as string,
                data: metric.PreviousSeries,
                borderColor: muted,
                borderWidth: 1.5,
                borderDash: [ 4, 4 ],
                fill: false,
                tension: 0.35,
                pointRadius: 0
            } );
        }

        return new Chart( canvas, {
            type: "line",
            data: {
                labels,
                datasets
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { intersect: false, mode: "index" },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: tooltip => `${ tooltip.dataset.label }: `
                                + ( isEuro
                                    ? this.euro( Number( tooltip.parsed.y ) )
                                    : String( tooltip.parsed.y ) )
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: {
                            color: muted,
                            font: { size: 11 },
                            maxRotation: 0,
                            autoSkip: false,
                            /* Keep it clean: only label the first and last tick. */
                            callback: ( value, index, ticks ) =>
                                index === 0 || index === ticks.length - 1
                                    ? labels[ index ]
                                    : ""
                        }
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: grid },
                        border: { display: false },
                        ticks: {
                            color: muted,
                            font: { size: 11 },
                            precision: isEuro ? undefined : 0,
                            callback: value => isEuro
                                ? this.euroShort( Number( value ) )
                                : String( value )
                        }
                    }
                }
            }
        } );
    }

    private buildStatusChart(): void {
        const canvas = this.$refs.statusCanvas as HTMLCanvasElement | undefined;

        if( canvas === undefined || this.statuses.length === 0 )
            return;

        const muted = this.themeColor( "--fg-muted", "#9A9EA2" );
        const surface = this.themeColor( "--bg-surface", "#FFFFFF" );

        this.statusChart = new Chart( canvas, {
            type: "doughnut",
            data: {
                labels: this.statuses.map( status => status.Status ),
                datasets: [ {
                    data: this.statuses.map( status => status.Count ),
                    backgroundColor: this.statuses.map(
                        status => STATUS_COLORS[ status.Status ] || muted ),
                    borderColor: surface,
                    borderWidth: 2
                } ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: "68%",
                plugins: {
                    legend: { position: "right",
                        labels: { color: muted, boxWidth: 12, usePointStyle: true } }
                }
            }
        } );
    }

    private buildPlanChart(): void {
        const canvas = this.$refs.planCanvas as HTMLCanvasElement | undefined;

        if( canvas === undefined || this.subscriptionsByPlan.length === 0 )
            return;

        const accent = this.themeColor( "--accent", "#FF6C00" );
        const grid = this.themeColor( "--border-subtle", "#EDEDED" );
        const muted = this.themeColor( "--fg-muted", "#9A9EA2" );

        this.planChart = new Chart( canvas, {
            type: "bar",
            data: {
                labels: this.subscriptionsByPlan.map( plan => plan.Name.trim() || "—" ),
                datasets: [ {
                    data: this.subscriptionsByPlan.map( plan => plan.Count ),
                    backgroundColor: accent,
                    borderRadius: 4,
                    barThickness: "flex",
                    maxBarThickness: 26
                } ]
            },
            options: {
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    x: {
                        beginAtZero: true,
                        grid: { color: grid },
                        border: { display: false },
                        ticks: { color: muted, font: { size: 11 }, precision: 0 }
                    },
                    y: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: { color: muted, font: { size: 11 } }
                    }
                }
            }
        } );
    }

    private buildRecurrenceChart(): void {
        const canvas = this.$refs.recurrenceCanvas as HTMLCanvasElement | undefined;

        if( canvas === undefined || this.mrrByRecurrence.length === 0 )
            return;

        const muted = this.themeColor( "--fg-muted", "#9A9EA2" );
        const surface = this.themeColor( "--bg-surface", "#FFFFFF" );

        this.recurrenceChart = new Chart( canvas, {
            type: "doughnut",
            data: {
                labels: this.mrrByRecurrence.map(
                    entry => this.$t( "HOME_recurrence" + entry.Recurrence ) as string ),
                datasets: [ {
                    data: this.mrrByRecurrence.map( entry => entry.Amount ),
                    backgroundColor: this.mrrByRecurrence.map(
                        entry => RECURRENCE_COLORS[ entry.Recurrence ] || muted ),
                    borderColor: surface,
                    borderWidth: 2
                } ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: "68%",
                plugins: {
                    legend: { position: "right",
                        labels: { color: muted, boxWidth: 12, usePointStyle: true } },
                    tooltip: {
                        callbacks: {
                            label: tooltip => `${ tooltip.label }: `
                                + this.euro( Number( tooltip.parsed ) )
                        }
                    }
                }
            }
        } );
    }

    /* "#RRGGBB" + alpha -> "rgba(...)". Passes through non-hex (e.g. rgb()) unchanged. */
    private hexToRgba( hex: string, alpha: number ): string {
        const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec( hex.trim() );

        if( match === null )
            return hex;

        const r = parseInt( match[ 1 ], 16 );
        const g = parseInt( match[ 2 ], 16 );
        const b = parseInt( match[ 3 ], 16 );

        return `rgba( ${ r }, ${ g }, ${ b }, ${ alpha } )`;
    }
}
